import {createContext, PropsWithChildren, useCallback, useContext, useState} from 'react';
import {DocumentNode, Kind} from 'graphql/language';
import {MutationHookOptions, TypedDocumentNode} from '@apollo/client';
import {toast} from 'sonner';

import {EnnablLogger} from 'utils/ennabl';

type RequestAnalyzer = {
  queries: string[];
  mutations: string[];
  addQuery: (query: DocumentNode | TypedDocumentNode) => void;
  addMutation: (query: DocumentNode | TypedDocumentNode, options?: MutationHookOptions<any, any>) => void;
};

const RequestAnalyzerContext = createContext<RequestAnalyzer>(null as unknown as RequestAnalyzer);

export const RequestAnalyzerProvider = ({children}: PropsWithChildren) => {
  const [queries, setQueries] = useState<RequestAnalyzer['queries']>([]);
  const [mutations, setMutations] = useState<RequestAnalyzer['mutations']>([]);

  const addQuery = useCallback<RequestAnalyzer['addQuery']>(query => {
    const operation = query.definitions.find(definition => definition.kind === Kind.OPERATION_DEFINITION);
    const name = operation?.name?.value;

    if (!name) {
      EnnablLogger.error('Operation definition not found', {query});
      toast.error('Request analyzer', {
        description: <strong>Operation definition not found</strong>,
      });
      return;
    }

    setQueries(prev => {
      if (prev.includes(name)) {
        return prev;
      }

      prev.push(name || 'Unnamed');

      return Array.from(new Set(prev));
    });
  }, []);

  const addMutation = useCallback<RequestAnalyzer['addMutation']>(mutation => {
    const operation = mutation.definitions.find(definition => definition.kind === Kind.OPERATION_DEFINITION);
    const name = operation?.name?.value;

    if (!name) {
      EnnablLogger.error('Operation definition not found', {mutation});
      toast.error('Request analyzer', {
        description: <strong>Operation definition not found</strong>,
      });
      return;
    }

    setMutations(prev => {
      if (prev.includes(name)) {
        return prev;
      }

      prev.push(name || 'Unnamed');

      return Array.from(new Set(prev));
    });
  }, []);

  return (
    <RequestAnalyzerContext.Provider
      value={{
        queries,
        mutations,
        addQuery,
        addMutation,
      }}
    >
      {children}
    </RequestAnalyzerContext.Provider>
  );
};

export const useRequestAnalyzer = () => {
  const context = useContext(RequestAnalyzerContext);

  if (!context) {
    return {
      queries: [],
      mutations: [],
      addQuery: () => {},
      addMutation: () => {},
    } satisfies RequestAnalyzer;
  }

  return context;
};
